import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import { getService, SERVICE_PAGES } from "@/data/services";
import { getPostByService } from "@/data/blog";
import { useSEO } from "@/lib/useSEO";
import { SITE_URL as SITE } from "@/lib/site";

export default function ServicePage() {
  const { slug } = useParams();
  const svc = getService(slug);

  useSEO(
    svc
      ? {
          title: svc.metaTitle,
          description: svc.metaDescription,
          keywords: svc.keywords,
          path: `/${svc.slug}`,
          image: `${SITE}/og-image.jpg`,
          jsonLd: {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Service",
                name: svc.h1,
                description: svc.metaDescription,
                serviceType: "Cybersecurity & Network Infrastructure",
                areaServed: "Worldwide",
                provider: { "@type": "Organization", name: "Endasphere", url: SITE },
                url: `${SITE}/${svc.slug}`,
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
                  { "@type": "ListItem", position: 2, name: svc.nav, item: `${SITE}/${svc.slug}` },
                ],
              },
              {
                "@type": "FAQPage",
                mainEntity: svc.faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          },
        }
      : { title: "Endasphere", path: `/${slug}` }
  );

  if (!svc) return <Navigate to="/" replace />;

  const related = SERVICE_PAGES.filter((s) => s.slug !== svc.slug).slice(0, 3);
  const guide = getPostByService(svc.slug);

  return (
    <article data-testid={`service-page-${svc.slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden pt-[140px] lg:pt-[180px] pb-20 lg:pb-28">
        <div className="absolute inset-0">
          <img src={svc.image} alt={svc.h1} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05050A]/80 via-[#05050A]/90 to-[#05050A]" />
        </div>
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 mb-10" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#22D3EE] transition-colors flex items-center gap-1.5" data-testid="breadcrumb-home">
              <ArrowLeft className="w-3.5 h-3.5" /> Home
            </Link>
            <span className="text-slate-700">/</span>
            <span className="text-[#22D3EE]">{svc.nav}</span>
          </nav>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10" style={{ backgroundColor: svc.accent }} />
              <span className="text-xs uppercase tracking-[0.3em]" style={{ color: svc.accent }}>Protection / {svc.tag}</span>
            </div>
            <h1 className="font-display font-900 text-white leading-[0.95] tracking-tighter text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
              {svc.h1}
            </h1>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">{svc.intro}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/#contact" data-testid="service-cta-demo" className="group inline-flex items-center gap-2 rounded-full bg-[#22D3EE] px-7 py-4 text-sm font-700 uppercase tracking-[0.12em] text-[#05050A] hover:bg-white transition-colors duration-300">
                Request a demo
                <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300" />
              </Link>
              <Link to="/#services" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-500 uppercase tracking-[0.12em] text-white hover:border-[#22D3EE] hover:text-[#22D3EE] transition-colors duration-300">
                All protection
              </Link>
              {guide && (
                <Link to={`/blog/${guide.slug}`} data-testid="service-guide-link" className="inline-flex items-center gap-2 px-2 py-4 text-sm font-500 uppercase tracking-[0.12em] text-slate-400 hover:text-[#22D3EE] transition-colors duration-300">
                  Read the guide
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-20 lg:py-28 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="font-display font-900 text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white leading-none mb-14">
            What you get
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
            {svc.features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                data-testid={`service-feature-${i}`}
                className="rounded-2xl border border-white/10 bg-[#0A0A12] p-8 flex gap-5"
              >
                <span className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${svc.accent}1a` }}>
                  <Check className="w-5 h-5" style={{ color: svc.accent }} />
                </span>
                <div>
                  <h3 className="font-display font-700 text-xl text-white tracking-tight">{f.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-20 lg:py-28 bg-[#07070E] border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="font-display font-900 text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white leading-none mb-12">
            {svc.nav} FAQ
          </h2>
          <div className="max-w-3xl">
            {svc.faqs.map((f, i) => (
              <div key={i} className="border-b border-white/10 py-7" data-testid={`service-faq-${i}`}>
                <h3 className="font-display font-700 text-lg sm:text-xl text-white tracking-tight">{f.q}</h3>
                <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed max-w-2xl">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related + CTA */}
      <section className="relative py-20 lg:py-28 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="text-xs uppercase tracking-[0.3em] text-[#22D3EE]">Explore more</span>
          <h2 className="mt-5 font-display font-900 text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white leading-none mb-12">
            Related protection
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/${r.slug}`}
                data-testid={`related-${r.slug}`}
                className="group rounded-2xl border border-white/10 bg-[#0A0A12] p-8 hover:border-[#22D3EE]/40 transition-colors duration-300"
              >
                <span className="font-mono text-xs tracking-[0.25em]" style={{ color: r.accent }}>/ {r.tag}</span>
                <h3 className="mt-6 font-display font-700 text-xl text-white tracking-tight group-hover:text-[#22D3EE] transition-colors duration-300">{r.h1}</h3>
                <ArrowUpRight className="mt-6 w-5 h-5 text-slate-600 group-hover:text-[#22D3EE] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
